const STORAGE_KEY = 'researchWizardDesigns';
const CURRENT_DESIGN_KEY = 'currentDesignId';
const SCHEMA_VERSION = 1;

const generateId = () => {
  return `design_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getDefaultDesignName = (index) => {
  const date = new Date().toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
  return `Research Design ${index} - ${date}`;
};

const createEmptyDesign = (name) => {
  return {
    id: generateId(),
    name: name || getDefaultDesignName(1),
    schemaVersion: SCHEMA_VERSION,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    wizardAComplete: false,
    wizardBComplete: false,
    wizardCComplete: false,
    wizardDComplete: false,
    currentStep: 0,
    wizardAData: {
      intake: {},
      philosophicalDimensions: {},
      philosophy: '',
      theoryApproach: '',
      methodologicalChoice: '',
      strategy: '',
      timeHorizon: '',
      techniques: {}
    },
    wizardBData: {
      researchQuestions: [],
      variables: [],
      assumptions: {},
      analysisPlan: {}
    },
    wizardCData: {
      researchAim: '',
      objectives: ['', '', '', '', '', '', ''],
      constructs: [],
      levels: [],
      diagramNodes: [],
      diagramConnections: [],
      assumptions: {},
      propositions: [],
      validationResults: {},
      generatedText: ''
    },
    wizardDData: {
      eligibility: {
        involvesHumanParticipants: null,
        involvesHumanTissue: null,
        involvesPersonalData: null
      },
      researchTeam: {
        principalInvestigator: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          institution: '',
          faculty: '',
          department: '',
          role: ''
        },
        supervisors: [],
        coResearchers: []
      },
      projectSynopsis: {
        title: '',
        startDate: '',
        endDate: '',
        ethicsStartDate: '',
        ethicsEndDate: '',
        fundingSource: '',
        fundingDetails: '',
        summary: '',
        researchQuestions: '',
        methodology: ''
      },
      participants: {
        description: '',
        selectionMethod: '',
        sampleSize: '',
        sampleSizeJustification: '',
        inclusionCriteria: '',
        exclusionCriteria: '',
        vulnerableGroups: {
          includes: false,
          types: [],
          justification: ''
        }
      },
      recruitment: {
        methods: [],
        otherMethod: '',
        initialInfo: '',
        consentProcess: '',
        withdrawalProcedure: '',
        debriefProcedure: ''
      },
      dataCollection: {
        methods: [],
        otherMethod: '',
        instruments: '',
        pilotTesting: '',
        interventionDetails: ''
      },
      dataManagement: {
        dataTypes: [],
        recordingMethod: '',
        anonymizationMethod: '',
        storageLocations: [],
        otherStorage: '',
        accessControl: '',
        securityMeasures: '',
        retentionPeriod: '',
        disposalMethod: '',
        gdprCompliant: null,
        privacyNoticeIncluded: null
      },
      riskAssessment: {
        participantRisks: [],
        participantMitigations: '',
        researcherRisks: '',
        researcherMitigations: '',
        incidentResponse: ''
      },
      specialConsiderations: {
        involvesMinors: false,
        minorsDetails: '',
        involvesHumanTissue: false,
        tissueDetails: '',
        involvesClinical: false,
        clinicalDetails: '',
        involvesIncentives: false,
        incentivesDetails: '',
        involvesInternationalData: false,
        internationalDetails: '',
        securitySensitive: false,
        securityDetails: ''
      },
      dissemination: {
        methods: [],
        otherMethod: '',
        dataSharing: '',
        embargoPlans: '',
        participantFeedback: ''
      },
      declarations: {
        confirmedAccuracy: false,
        confirmedGDPR: false,
        confirmedTraining: false,
        additionalComments: ''
      }
    },
    decisions: []
  };
};

export const designStorageService = {
  getAllDesigns: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return [];
      const designs = JSON.parse(data);
      return designs.filter(d => d && d.id);
    } catch (error) {
      console.error('Error loading designs:', error);
      return [];
    }
  },

  getDesignById: (id) => {
    const designs = designStorageService.getAllDesigns();
    return designs.find(d => d.id === id) || null;
  },

  saveDesign: (design) => {
    try {
      const designs = designStorageService.getAllDesigns();
      const existingIndex = designs.findIndex(d => d.id === design.id);
      
      const updatedDesign = {
        ...design,
        updatedAt: new Date().toISOString(),
        schemaVersion: SCHEMA_VERSION
      };
      
      if (existingIndex >= 0) {
        designs[existingIndex] = updatedDesign;
      } else {
        designs.push(updatedDesign);
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(designs));
      return updatedDesign;
    } catch (error) {
      console.error('Error saving design:', error);
      throw new Error('Failed to save design');
    }
  },

  createNewDesign: (name) => {
    const designs = designStorageService.getAllDesigns();
    const newName = name || getDefaultDesignName(designs.length + 1);
    const newDesign = createEmptyDesign(newName);
    return designStorageService.saveDesign(newDesign);
  },

  duplicateDesign: (id, newName) => {
    const original = designStorageService.getDesignById(id);
    if (!original) throw new Error('Design not found');
    
    const duplicate = {
      ...original,
      id: generateId(),
      name: newName || `${original.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    return designStorageService.saveDesign(duplicate);
  },

  renameDesign: (id, newName) => {
    const design = designStorageService.getDesignById(id);
    if (!design) throw new Error('Design not found');
    
    return designStorageService.saveDesign({
      ...design,
      name: newName
    });
  },

  deleteDesign: (id) => {
    try {
      const designs = designStorageService.getAllDesigns();
      const filtered = designs.filter(d => d.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      
      if (designStorageService.getCurrentDesignId() === id) {
        designStorageService.setCurrentDesignId(null);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting design:', error);
      throw new Error('Failed to delete design');
    }
  },

  getCurrentDesignId: () => {
    return localStorage.getItem(CURRENT_DESIGN_KEY);
  },

  setCurrentDesignId: (id) => {
    if (id) {
      localStorage.setItem(CURRENT_DESIGN_KEY, id);
    } else {
      localStorage.removeItem(CURRENT_DESIGN_KEY);
    }
  },

  exportDesign: (id) => {
    const design = designStorageService.getDesignById(id);
    if (!design) throw new Error('Design not found');
    
    const blob = new Blob([JSON.stringify(design, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${design.name.replace(/[^a-z0-9]/gi, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importDesign: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const design = JSON.parse(e.target.result);
          if (!design.wizardAData || !design.wizardBData) {
            throw new Error('Invalid design file format');
          }
          
          const importedDesign = {
            ...design,
            id: generateId(),
            name: design.name + ' (Imported)',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            schemaVersion: SCHEMA_VERSION
          };
          
          const saved = designStorageService.saveDesign(importedDesign);
          resolve(saved);
        } catch (error) {
          reject(new Error('Failed to import design: ' + error.message));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }
};

export default designStorageService;
